import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  ENERGY_OVERPAYMENT_DISCLAIMER,
  ENERGY_ZONES,
  isOutOfScope,
  loyaltyTaxBullet,
  type LoyaltyTaxOutput,
  outOfScopeBullet,
  overpaymentBullet,
  overpaymentHeadline,
  type OverpaymentResult,
  sourceBullet,
} from "@aucash/rules/energy-overpayment";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Field, MoneyInput, OptionList, Segmented } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// energy-overpayment — the AER DMO reference-price check + ACCC loyalty-tax,
// surfaced stateless-v1 (PLAN arch decision #1). One screen, two modes over the
// dated DMO ruleset; the result renders INLINE (no save-by-id, no auth gate, no
// persistence). Each compute is a stateless `calc.energyOverpayment.compute`
// query — input -> output, no round-trip to a stored row.
//
// Regulatory posture (validation §3, binding): every output is a CITED FACTUAL
// COMPARISON, information not advice — "your plan is $X vs the DMO reference of
// $Y", NEVER "switch and save $X". Copy comes verbatim from the rules layer's
// copy helpers so the wording can never drift from the API/SEO surfaces. The
// energy-switch affiliate (Econnex) is a PERMITTED but NON-FINANCIAL handoff and
// is kept OFF this compute surface entirely — there is no "switch / compare"
// CTA on a personalised result here (the rules-layer no-affiliate guard holds at
// the UI). We compare to the REGULATED REFERENCE (DMO), not a live market scrape,
// and Victoria (VDO, not DMO) renders the explicit out-of-scope copy.

// The assessment date drives which dated DMO determination is in force (never
// Date.now() — passed in, so the figure is reproducible). DMO 8 governs
// 1 Jul 2026 – 30 Jun 2027.
const ASSESSMENT_DATE = "2026-09-01";

type Mode = "overpayment" | "loyalty-tax";

const MODE_OPTIONS: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: "overpayment", label: "Vs the reference" },
  { value: "loyalty-tax", label: "Loyalty tax" },
];

type PlanMode = "annual" | "usage";

const PLAN_MODE_OPTIONS: ReadonlyArray<{ value: PlanMode; label: string }> = [
  { value: "annual", label: "I know my annual $" },
  { value: "usage", label: "From my bill rates" },
];

type Zone = (typeof ENERGY_ZONES)[number];

/** Human labels for the zone picker. Victoria is in the list but renders the
 *  out-of-scope copy on submit (VDO, not DMO). */
const ZONE_LABELS: Record<Zone, { label: string; sub: string }> = {
  AUSGRID: { label: "Ausgrid (NSW)", sub: "Sydney, Central Coast, Hunter" },
  ENDEAVOUR: {
    label: "Endeavour Energy (NSW)",
    sub: "Greater western Sydney, Blue Mountains, Illawarra",
  },
  ESSENTIAL: { label: "Essential Energy (NSW)", sub: "Regional NSW" },
  ENERGEX: { label: "Energex (SE QLD)", sub: "Brisbane / South East Queensland" },
  SA_POWER_NETWORKS: { label: "SA Power Networks", sub: "South Australia" },
  VICTORIA: { label: "Victoria", sub: "Uses the VDO, not the DMO — out of scope" },
};

const ZONE_OPTIONS = ENERGY_ZONES.map((z) => ({
  value: z,
  label: ZONE_LABELS[z].label,
  sub: ZONE_LABELS[z].sub,
}));

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.energyOverpayment.compute.query>
>;

export default function EnergyOverpaymentIndex(): JSX.Element {
  const router = useRouter();

  const [mode, setMode] = useState<Mode>("overpayment");
  const [zone, setZone] = useState<Zone | null>(null);
  const [planMode, setPlanMode] = useState<PlanMode>("annual");

  // annual path
  const [annualCost, setAnnualCost] = useState("");

  // usage path
  const [supplyCharge, setSupplyCharge] = useState("");
  const [usageRate, setUsageRate] = useState("");
  const [annualKwh, setAnnualKwh] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const onModeChange = (next: Mode): void => {
    setMode(next);
    setResult(null);
    setSubmitError(null);
    setErrors({});
  };

  const buildInput = (): {
    input: Parameters<typeof trpc.calc.energyOverpayment.compute.query>[0]["input"];
  } | null => {
    if (mode === "loyalty-tax") {
      return { input: { mode: "loyalty-tax", assessmentDate: ASSESSMENT_DATE } };
    }
    if (zone === null) return null;
    if (planMode === "usage") {
      return {
        input: {
          mode: "overpayment",
          zone,
          assessmentDate: ASSESSMENT_DATE,
          usage: {
            dailySupplyCharge: num(supplyCharge),
            usageRatePerKwh: num(usageRate),
            annualUsageKwh: num(annualKwh),
          },
        },
      };
    }
    return {
      input: {
        mode: "overpayment",
        zone,
        assessmentDate: ASSESSMENT_DATE,
        currentAnnualCost: num(annualCost),
      },
    };
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (mode === "loyalty-tax") return next;
    if (zone === null) next.zone = "Choose your electricity distribution zone.";
    if (planMode === "annual") {
      if (annualCost.trim() === "")
        next.annual = "Enter your plan's annual electricity cost.";
    } else {
      if (supplyCharge.trim() === "")
        next.supply = "Enter your daily supply charge.";
      if (usageRate.trim() === "") next.rate = "Enter your usage rate.";
      if (annualKwh.trim() === "") next.kwh = "Enter your annual usage in kWh.";
    }
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    const payload = buildInput();
    if (Object.keys(next).length > 0 || payload === null || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.energyOverpayment.compute
      .query(payload)
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Energy check" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>DMO 8 · 2026-27 reference price</Eyebrow>
            <Text style={styles.title}>
              {mode === "overpayment"
                ? "Are you above the electricity reference price?"
                : "What's the ACCC loyalty tax?"}
            </Text>
            <Text style={styles.subtitle}>
              {mode === "overpayment"
                ? "Compare your plan's yearly cost against the AER's regulated Default Market Offer (DMO) reference price for your distribution zone. This is a factual comparison to the regulated benchmark — not a live comparison of every retailer's market offer."
                : "The ACCC reports that customers on old electricity plans pay more, on average, than customers on new plans. See the cited range — a population average, not an estimate of your own bill."}
            </Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="What are you checking?">
            <Segmented<Mode>
              options={MODE_OPTIONS}
              value={mode}
              onChange={onModeChange}
              testIDPrefix="mode"
            />
          </Field>

          {mode === "overpayment" ? (
            <>
              <Field
                label="Your distribution zone"
                help="The network that delivers your electricity — set by where you live, not who you buy from. Victoria uses a different scheme (the VDO) and is out of scope."
                error={errors.zone}
              >
                <OptionList<Zone>
                  options={ZONE_OPTIONS}
                  value={zone}
                  onChange={(z) => {
                    setZone(z);
                    setResult(null);
                  }}
                  testIDPrefix="zone"
                />
              </Field>

              <Field label="How do you want to enter your plan?">
                <Segmented<PlanMode>
                  options={PLAN_MODE_OPTIONS}
                  value={planMode}
                  onChange={(m) => {
                    setPlanMode(m);
                    setResult(null);
                  }}
                  testIDPrefix="planmode"
                />
              </Field>

              {planMode === "annual" ? (
                <Field
                  label="Your plan's annual electricity cost"
                  help="Your expected total electricity cost for the year — from your bill's annual summary or your retailer's basic plan information document."
                  error={errors.annual}
                >
                  <MoneyInput
                    value={annualCost}
                    onChange={setAnnualCost}
                    testID="input-annual-cost"
                    accessibilityLabel="Your plan's annual electricity cost"
                  />
                </Field>
              ) : (
                <>
                  <Field
                    label="Daily supply charge ($/day)"
                    help="The fixed daily service-to-property charge on your bill. We annualise it over 365 days."
                    error={errors.supply}
                  >
                    <MoneyInput
                      value={supplyCharge}
                      onChange={setSupplyCharge}
                      testID="input-supply-charge"
                      accessibilityLabel="Daily supply charge in dollars per day"
                    />
                  </Field>
                  <Field
                    label="Usage rate ($/kWh)"
                    help="Your consumption rate in DOLLARS per kWh — e.g. enter 0.30 for 30c/kWh."
                    error={errors.rate}
                  >
                    <MoneyInput
                      value={usageRate}
                      onChange={setUsageRate}
                      testID="input-usage-rate"
                      accessibilityLabel="Usage rate in dollars per kilowatt hour"
                    />
                  </Field>
                  <Field
                    label="Your annual usage (kWh)"
                    help="How many kilowatt-hours you use across the year — on your bill, or roughly 4,000–7,000 kWh for a typical household."
                    error={errors.kwh}
                  >
                    <MoneyInput
                      value={annualKwh}
                      onChange={setAnnualKwh}
                      testID="input-annual-kwh"
                      accessibilityLabel="Your annual electricity usage in kilowatt hours"
                    />
                  </Field>
                </>
              )}
            </>
          ) : (
            <Text style={styles.bodyText}>
              No details needed — the loyalty-tax figures are an ACCC population
              average. Tap below to see the cited range.
            </Text>
          )}

          <Button
            label={submitting ? "Calculating…" : "See the comparison →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="energy-overpayment-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer (rules-layer ENERGY_OVERPAYMENT_DISCLAIMER) sits at the
 * TOP, before any number (validation §3.2 placement rule). Victoria renders the
 * out-of-scope copy instead of a DMO comparison. */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const isLoyalty = result.mode === "loyalty-tax";
  const overpayment = !isLoyalty
    ? (result.output as OverpaymentResult)
    : null;
  const victoria = overpayment !== null && isOutOfScope(overpayment);

  const headline = isLoyalty
    ? "What the ACCC reports about old plans"
    : victoria
      ? "Victoria isn't covered by the DMO"
      : overpaymentHeadline(overpayment as OverpaymentResult);

  const bullet = isLoyalty
    ? loyaltyTaxBullet(result.output as LoyaltyTaxOutput)
    : victoria
      ? outOfScopeBullet()
      : overpaymentBullet(overpayment as OverpaymentResult);

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{ENERGY_OVERPAYMENT_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>{isLoyalty ? "ACCC · cited range" : "Vs the DMO reference"}</Eyebrow>
        <Chip label={victoria ? "Out of scope" : "Comparison"} tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline}</Text>
        <Text style={styles.bodyText}>{bullet}</Text>
      </Card>

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          {sourceBullet(
            result.determination,
            result.period,
            result.sourcesVerifiedAt,
          )}
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
